// Redux duck for ListingWizardPage
import { storableError } from '../../util/errors';
import { UploadImage, UploadVideo } from '../../util/cloudinary';
import { v4 as uuid4 } from 'uuid';
import { createAlgoliaData, enhanceField, generateAIImages, generateAIListing, getCredits, getUserAchievements, sendSMSNotification, syncListingAlgolia, updateCloudinaryContext, userActiveListings } from '../../util/api';
import { buildListingImageSeoTags, buildListingVideoSeoTags } from '../../util/imageSeoTags';
import { generateAchievementReviews } from '../../util/reviews';
import { denormalisedResponseEntities, extractLocationComponents, getEnhancedContent, isUploadedInstructionVideo, urlToFile } from '../../util/data';
import { fetchExchangeRates } from '../../ducks/exchangeRates.duck';
import moment from 'moment';
import { IS_DEVELOPMENT_ENV, LISTING_ALOGLIA_REMOVABLE_FIELDS, DEFAULT_AVAILABILITY_PLAN } from '../../util/constants';
import { getAlgoliaIndexNameForLang } from '../../util/urlHelpers';
import { setPublishedListingsCount } from '../../util/mixpanel';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { filterUserPublicDataForAlgolia, filterListingUserPublicDataForAlgolia, filterPublicDataToAlgolia } from '../../util/algoliaHelpers';
const listingSmsNotificationContentSid = process.env.REACT_APP_TWILIO_listing_is_live_en;


// Action types
const SAVE_INPUT = 'ListingWizardPage/SAVE_INPUT';
const UPLOAD_IMAGES_REQUEST = 'ListingWizardPage/UPLOAD_IMAGES_REQUEST';
const UPLOAD_IMAGES_SUCCESS = 'ListingWizardPage/UPLOAD_IMAGES_SUCCESS';
const UPLOAD_IMAGES_ERROR = 'ListingWizardPage/UPLOAD_IMAGES_ERROR';
const GENERATE_LISTING_REQUEST = 'ListingWizardPage/GENERATE_LISTING_REQUEST';
const GENERATE_LISTING_SUCCESS = 'ListingWizardPage/GENERATE_LISTING_SUCCESS';
const GENERATE_LISTING_ERROR = 'ListingWizardPage/GENERATE_LISTING_ERROR';
const RESET_STATE = 'ListingWizardPage/RESET_STATE';
// About Owner section
const FETCH_OWNER_INFO_REQUEST = 'ListingWizardPage/FETCH_OWNER_INFO_REQUEST';
const FETCH_OWNER_INFO_SUCCESS = 'ListingWizardPage/FETCH_OWNER_INFO_SUCCESS';
const FETCH_OWNER_INFO_ERROR = 'ListingWizardPage/FETCH_OWNER_INFO_ERROR';

// Save Draft
const SAVE_LISTING_DRAFT_REQUEST = 'ListingWizardPage/SAVE_LISTING_DRAFT_REQUEST';
const SAVE_LISTING_DRAFT_SUCCESS = 'ListingWizardPage/SAVE_LISTING_DRAFT_SUCCESS';
const SAVE_LISTING_DRAFT_ERROR = 'ListingWizardPage/SAVE_LISTING_DRAFT_ERROR';

const CREATE_LISTING_REQUEST = 'ListingWizardPage/CREATE_LISTING_REQUEST';
const CREATE_LISTING_SUCCESS = 'ListingWizardPage/CREATE_LISTING_SUCCESS';
const CREATE_LISTING_ERROR = 'ListingWizardPage/CREATE_LISTING_ERROR';

const GET_CREDITS_REQUEST = 'ListingWizardPage/GET_CREDITS_REQUEST';
const GET_CREDITS_SUCCESS = 'ListingWizardPage/GET_CREDITS_SUCCESS';
const GET_CREDITS_ERROR = 'ListingWizardPage/GET_CREDITS_ERROR';

const ENHANCE_FIELD_REQUEST = 'ListingWizardPage/ENHANCE_FIELD_REQUEST';
const ENHANCE_FIELD_SUCCESS = 'ListingWizardPage/ENHANCE_FIELD_SUCCESS';
const ENHANCE_FIELD_ERROR = 'ListingWizardPage/ENHANCE_FIELD_ERROR';

const GENERATING_AI_IMAGES_REQUEST = 'ListingWizardPage/GENERATING_AI_IMAGES_REQUEST';
const GENERATING_AI_IMAGES_SUCCESS = 'ListingWizardPage/GENERATING_AI_IMAGES_SUCCESS';
const GENERATING_AI_IMAGES_ERROR = 'ListingWizardPage/GENERATING_AI_IMAGES_ERROR';
const CLEAR_GENERATED_IMAGES = 'ListingWizardPage/CLEAR_GENERATED_IMAGES';

// Draft operations (for editing flow)
const UPDATE_DRAFT_REQUEST = 'ListingWizardPage/UPDATE_DRAFT_REQUEST';
const UPDATE_DRAFT_SUCCESS = 'ListingWizardPage/UPDATE_DRAFT_SUCCESS';
const UPDATE_DRAFT_ERROR = 'ListingWizardPage/UPDATE_DRAFT_ERROR';

const PUBLISH_DRAFT_REQUEST = 'ListingWizardPage/PUBLISH_DRAFT_REQUEST';
const PUBLISH_DRAFT_SUCCESS = 'ListingWizardPage/PUBLISH_DRAFT_SUCCESS';
const PUBLISH_DRAFT_ERROR = 'ListingWizardPage/PUBLISH_DRAFT_ERROR';

// Edit listing data (for editing tabs)
const MERGE_EDIT_LISTING_DATA = 'ListingWizardPage/MERGE_EDIT_LISTING_DATA';
const RESET_EDIT_LISTING_DATA = 'ListingWizardPage/RESET_EDIT_LISTING_DATA';

// Initial state
const initialState = {
    input: null,
    imagesUploading: false,
    imagesError: null,
    images: [],
    sharetribeImageIds: [],
    generating: false,
    generateError: null,
    generatedListing: {},
    listingToken: null,
    retryCount: 0,
    lastError: null,
    ownerInfo: null,
    saveListingDraftInProgress: false,
    saveListingDraftError: null,
    createdListing: null,
    createListingError: null,
    createListingInProgress: false,
    credits: {},
    creditsError: null,
    creditsLoading: false,

    enhanceFieldInProgress: {},
    enhanceFieldError: {},

    generatedImages: [],
    generateImagesError: null,
    generateImagesInProgress: false,
    photosTabVisited: false,

    // Draft editing state
    draftListingId: null,
    draftSaving: false,
    draftError: null,

    // Edit listing data (accumulated state from editing tabs)
    editListingData: {},
};



// Reducer
export default function reducer(state = initialState, action = {}) {
    switch (action.type) {
        case SAVE_INPUT:
            return { ...state, input: action.payload };
        case UPLOAD_IMAGES_REQUEST:
            return {
                ...state,
                imagesUploading: true,
                imagesError: null,
                lastError: null
            };
        case UPLOAD_IMAGES_SUCCESS:
            return {
                ...state,
                imagesUploading: false,
                images: [...state.images, ...action.payload.images],
                sharetribeImageIds: [...state.sharetribeImageIds, ...(action.payload.sharetribeImageIds || [])],
                retryCount: 0,
                lastError: null
            };
        case UPLOAD_IMAGES_ERROR:
            return {
                ...state,
                imagesUploading: false,
                imagesError: action.payload,
                lastError: action.payload,
                retryCount: state.retryCount + 1
            };
        case GENERATE_LISTING_REQUEST:
            return {
                ...state,
                generating: true,
                generateError: null,
                lastError: null,
                generatedListing: {},  // Clear old generated listing when starting new generation
                listingToken: null,     // Clear old listing token when starting new generation
            };
        case GENERATE_LISTING_SUCCESS:
            return {
                ...state,
                generating: false,
                generatedListing: action.payload.content,
                listingToken: action.payload.listingToken,
                retryCount: 0,
                lastError: null
            };
        case GENERATE_LISTING_ERROR:
            return {
                ...state,
                generating: false,
                generateError: action.payload,
                lastError: action.payload,
                retryCount: state.retryCount + 1
            };
        case RESET_STATE:
            return {
                ...initialState,
                editListingData: {}, // Also reset editListingData when resetting state
            };
        case FETCH_OWNER_INFO_REQUEST:
            return { ...state, ownerInfoLoading: true, ownerInfoError: null };
        case FETCH_OWNER_INFO_SUCCESS:
            return {
                ...state,
                ownerInfo: action.payload,
                ownerInfoLoading: false,
            };
        case FETCH_OWNER_INFO_ERROR:
            return { ...state, ownerInfoLoading: false, ownerInfoError: action.payload };
        case SAVE_LISTING_DRAFT_REQUEST:
            return { ...state, saveListingDraftInProgress: true, saveListingDraftError: null };
        case SAVE_LISTING_DRAFT_SUCCESS:
            return { ...state, saveListingDraftInProgress: false };
        case SAVE_LISTING_DRAFT_ERROR:
            return { ...state, saveListingDraftInProgress: false, saveListingDraftError: action.payload };
        case CREATE_LISTING_REQUEST:
            return { ...state, createListingInProgress: true, createListingError: null };
        case CREATE_LISTING_SUCCESS:
            return { ...state, createListingInProgress: false, createdListing: action.payload };
        case CREATE_LISTING_ERROR:
            return { ...state, createListingInProgress: false, createListingError: action.payload };
        case GET_CREDITS_REQUEST:
            return { ...state, creditsLoading: true, creditsError: null };
        case GET_CREDITS_SUCCESS:
            return {
                ...state,
                creditsLoading: false,
                credits: {
                    ...action.payload,
                    maxImageCredits: action.payload.maxImageCredits ?? state.credits?.maxImageCredits ?? 3,
                },
            };
        case GET_CREDITS_ERROR:
            return { ...state, creditsLoading: false, creditsError: action.payload };

        case ENHANCE_FIELD_REQUEST:
            return {
                ...state,
                enhanceFieldInProgress: {
                    ...state.enhanceFieldInProgress,
                    [action.payload.field]: true,
                },
            };
        case ENHANCE_FIELD_SUCCESS:
            return {
                ...state,
                enhanceFieldInProgress: {
                    ...state.enhanceFieldInProgress,
                    [action.payload.field]: false,
                },
                generatedListing: {
                    ...state.generatedListing,
                    [action.payload.field]: getEnhancedContent(action.payload.field, action.payload.enhancedContent, state.generatedListing?.online_review_summary?.rating),
                },
            };
        case ENHANCE_FIELD_ERROR:
            return {
                ...state,
                enhanceFieldInProgress: {
                    ...state.enhanceFieldInProgress,
                    [action.payload.field]: false,
                },
                enhanceFieldError: {
                    ...state.enhanceFieldError,
                    [action.payload.field]: action.payload.error,
                },
            };

        case GENERATING_AI_IMAGES_REQUEST:
            return {
                ...state,
                generateImagesInProgress: true,
                generateImagesError: null,
            };
        case GENERATING_AI_IMAGES_SUCCESS:
            return {
                ...state,
                generateImagesInProgress: false,
                generatedImages: action.payload,
            };
        case GENERATING_AI_IMAGES_ERROR:
            return {
                ...state,
                generateImagesInProgress: false,
                generateImagesError: action.payload,
            };
        case CLEAR_GENERATED_IMAGES:
            return { ...state, generatedImages: [], photosTabVisited: true };
        case UPDATE_DRAFT_REQUEST:
            return {
                ...state,
                draftSaving: true,
                draftError: null,
            };
        case UPDATE_DRAFT_SUCCESS:
            return {
                ...state,
                draftSaving: false,
                draftListingId: action.payload?.id?.uuid || action.payload?.data?.data?.id?.uuid || state.draftListingId,
            };
        case UPDATE_DRAFT_ERROR:
            return {
                ...state,
                draftSaving: false,
                draftError: action.payload,
            };
        case PUBLISH_DRAFT_REQUEST:
            return {
                ...state,
                draftSaving: true,
                draftError: null,
            };
        case PUBLISH_DRAFT_SUCCESS:
            return {
                ...state,
                draftSaving: false,
                draftListingId: null, // Clear draft ID after publishing
            };
        case PUBLISH_DRAFT_ERROR:
            return {
                ...state,
                draftSaving: false,
                draftError: action.payload,
            };
        case MERGE_EDIT_LISTING_DATA:
            // Don't merge if payload is empty or invalid - preserve existing state
            if (!action.payload || (typeof action.payload === 'object' && Object.keys(action.payload).length === 0)) {
                return state;
            }

            return {
                ...state,
                editListingData: {
                    ...state.editListingData,
                    ...action.payload,
                    // Deep merge publicData - preserve existing if payload doesn't have it
                    publicData: action.payload.publicData
                        ? {
                            ...(state.editListingData?.publicData || {}),
                            ...action.payload.publicData,
                        }
                        : (state.editListingData?.publicData || {}), // Preserve existing publicData if payload doesn't have it
                }
            };
        case RESET_EDIT_LISTING_DATA:
            return {
                ...state,
                editListingData: {}
            };
        default:
            return state;
    }
}

// Action creators
export const saveInput = payload => ({ type: SAVE_INPUT, payload });
export const uploadImagesRequest = () => ({ type: UPLOAD_IMAGES_REQUEST });
export const uploadImagesSuccess = payload => ({ type: UPLOAD_IMAGES_SUCCESS, payload });
export const uploadImagesError = error => ({ type: UPLOAD_IMAGES_ERROR, payload: storableError(error) });
export const generateListingRequest = () => ({ type: GENERATE_LISTING_REQUEST });
export const generateListingSuccess = payload => ({ type: GENERATE_LISTING_SUCCESS, payload });
export const generateListingError = error => ({ type: GENERATE_LISTING_ERROR, payload: storableError(error) });
export const resetState = () => ({ type: RESET_STATE });
export const fetchOwnerInfoRequest = () => ({ type: FETCH_OWNER_INFO_REQUEST });
export const fetchOwnerInfoSuccess = payload => ({ type: FETCH_OWNER_INFO_SUCCESS, payload });
export const fetchOwnerInfoError = error => ({ type: FETCH_OWNER_INFO_ERROR, payload: storableError(error) });
export const saveListingDraftRequest = () => ({ type: SAVE_LISTING_DRAFT_REQUEST });
export const saveListingDraftSuccess = payload => ({ type: SAVE_LISTING_DRAFT_SUCCESS, payload });
export const saveListingDraftError = error => ({ type: SAVE_LISTING_DRAFT_ERROR, payload: !!error ? storableError(error) : null });

export const createListingRequest = () => ({ type: CREATE_LISTING_REQUEST });
export const createListingSuccess = (data) => ({ type: CREATE_LISTING_SUCCESS, payload: data.data });
export const createListingError = (error) => ({ type: CREATE_LISTING_ERROR, payload: !!error ? storableError(error) : null });

export const getCreditsRequest = () => ({ type: GET_CREDITS_REQUEST });
export const getCreditsSuccess = payload => ({ type: GET_CREDITS_SUCCESS, payload });
export const getCreditsError = error => ({ type: GET_CREDITS_ERROR, payload: storableError(error) });

export const enhanceFieldRequest = (field) => ({ type: ENHANCE_FIELD_REQUEST, payload: { field } });
export const enhanceFieldSuccess = (field, enhancedContent) => ({ type: ENHANCE_FIELD_SUCCESS, payload: { field, enhancedContent } });
export const enhanceFieldError = (field, error) => ({ type: ENHANCE_FIELD_ERROR, payload: { field, error: storableError(error) } });

export const generateAIImagesRequest = () => ({ type: GENERATING_AI_IMAGES_REQUEST });
export const generateAIImagesSuccess = (data) => ({ type: GENERATING_AI_IMAGES_SUCCESS, payload: data });
export const generateAIImagesError = (error) => ({ type: GENERATING_AI_IMAGES_ERROR, payload: storableError(error) });
export const clearGeneratedImages = () => ({ type: CLEAR_GENERATED_IMAGES });

export const updateDraftRequest = () => ({ type: UPDATE_DRAFT_REQUEST });
export const updateDraftSuccess = (data) => ({ type: UPDATE_DRAFT_SUCCESS, payload: data });
export const updateDraftError = (error) => ({ type: UPDATE_DRAFT_ERROR, payload: storableError(error) });

export const publishDraftRequest = () => ({ type: PUBLISH_DRAFT_REQUEST });
export const publishDraftSuccess = (data) => ({ type: PUBLISH_DRAFT_SUCCESS, payload: data });
export const publishDraftError = (error) => ({ type: PUBLISH_DRAFT_ERROR, payload: storableError(error) });

// Edit listing data actions
export const mergeEditListingData = (data) => ({ type: MERGE_EDIT_LISTING_DATA, payload: data });
export const resetEditListingData = () => ({ type: RESET_EDIT_LISTING_DATA });

// Constants
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

// Thunks
export const uploadImages = (images) => async (dispatch, getState, sdk) => {
    try {
        if (!images?.length) return [];

        dispatch(uploadImagesRequest());

        const uploadImage = async (file, index) => {
            // First upload to sharetribe
            const imageRes = await sdk.images.upload({ image: file });
            const id = imageRes.data.data.id.uuid;

            // Then upload to cloudinary - only return if BOTH succeed
            const result = await UploadImage({ file, id });
            if (!result || !result.secure_url) {
                throw new Error('Cloudinary upload failed');
            }

            // Build a synthetic listing context from Redux state to pass to the tag builder
            const wState = getState().ListingWizardPage;
            const listingContext = {
                attributes: {
                    title: wState.generatedListing?.title || wState.editListingData?.title || '',
                    publicData: {
                        city: wState.editListingData?.publicData?.city || '',
                        category: wState.editListingData?.publicData?.category || '',
                        condition: wState.editListingData?.publicData?.condition || '',
                    },
                },
            };
            const storeName = getState().user.currentUser?.attributes?.profile?.publicData?.storeName || '';
            const seoTags = buildListingImageSeoTags(listingContext, index, storeName);

            // Write to Cloudinary context — fire-and-forget
            updateCloudinaryContext({ publicId: id, context: seoTags })
                .catch(err => console.error('Cloudinary context update failed:', err));

            // Only return if both uploads succeeded
            return {
                public_id: id,
                secure_url: result.secure_url,
                height: result.height,
                width: result.width,
                sharetribeImgId: id,
                seoTags,
            };
        };

        const uploadedImages = await Promise.all(
            images.map((file, index) => uploadImage(file, index))
        );

        // Merge uploaded images into editListingData.publicData.listingImages
        const existingListingImages =
            getState().ListingWizardPage.editListingData?.publicData?.listingImages || [];

        const mergedListingImages = [...existingListingImages];
        uploadedImages.forEach(newImg => {
            const idx = mergedListingImages.findIndex(e => e.public_id === newImg.public_id);
            if (idx >= 0) {
                mergedListingImages[idx] = newImg;
            } else {
                mergedListingImages.push(newImg);
            }
        });

        dispatch(mergeEditListingData({ publicData: { listingImages: mergedListingImages } }));

        // Extract sharetribeImageIds after all uploads complete successfully
        const sharetribeImageIds = uploadedImages.map(img => img.sharetribeImgId);

        dispatch(uploadImagesSuccess({ images: uploadedImages, sharetribeImageIds }));
        return uploadedImages;
    } catch (error) {
        console.error('Failed to upload images:', error);
        dispatch(uploadImagesError(error));
        // Re-throw to prevent continuing with failed uploads
        throw error;
    }
};

export const generateListing = input => async (dispatch, getState, sdk) => {
    try {
        dispatch(generateListingRequest());

        const { currentUserCurrency = 'USD' } = getState().user;

        let finalInputData = {
            productDescription: input.productDescription,
            location: input?.location?.selectedPlace?.city || extractLocationComponents(input?.location?.selectedPlace?.address),
            externalProductUrl: input.externalProductUrl,

            // Optional content flags
            includeHowToUse: input.includeHowToUse,
            includeSuggestedUseCases: input.includeSuggestedUseCases,
            includeRentalRates: input.includeRentalRates,
            includeReviewSummary: input.includeReviewSummary,
            includeLocationContext: input.includeLocationContext,
            includeFaqs: input.includeFaqs,
            includeAlternativeTitles: input.includeAlternativeTitles,
            includeReturnCareInstructions: input.includeReturnCareInstructions,

            // Suggestion based fields options
            itemCategoryOptions: input.itemCategoryOptions,
            itemConditionOptions: input.itemConditionOptions,
            itemReplacementValueOptions: input.itemReplacementValueOptions,
            currency: currentUserCurrency,
            locale: input.locale,
        };


        const { data } = await generateAIListing(finalInputData, input.images);

        const { listingToken, content, images } = data;

        if (!listingToken || !content) {
            throw new Error('Invalid listing data received');
        }

        dispatch(generateListingSuccess({ listingToken, content }));

        if (images?.length) {
            const generatedImages = images.map(img => ({
                public_id: img.publicId,
                secure_url: img.imageUrl,
                height: img.height,
                width: img.width,
                ...(img.seoTags && { seoTags: img.seoTags }),
            }));

            dispatch(generateAIImagesSuccess(generatedImages));
        }

        return { listingToken, content, images };
    } catch (error) {
        dispatch(generateListingError(error));
        throw error;
    }
};

// Thunk to fetch current user's profile, listings count, reviews, rating, and verification
export const fetchOwnerInfo = () => async (dispatch, getState, sdk) => {
    try {
        dispatch(fetchOwnerInfoRequest());
        const state = getState();
        const { isAuthenticated } = state.auth;
        const currentUser = state.user.currentUser;

        if (!isAuthenticated || !currentUser || !currentUser.id) {
            throw new Error('User not authenticated');
        }

        const userId = currentUser.id.uuid;

        // Parallel API calls
        const [listingsRes, achievementsRes, reviewsRes] = await Promise.all([
            sdk.listings.query({ authorId: userId, perPage: 1 }),
            getUserAchievements(userId),
            sdk.reviews.query({
                subjectId: userId,
                type: 'ofProvider',
                state: 'public',
                include: ['author', 'author.profileImage'],
                'fields.image': ['variants.square-small', 'variants.square-small2x'],
            })
        ]);

        const totalListings = listingsRes.data.meta.totalItems;
        const { emailVerified, stripeConnected, publishedListings, displayName } = achievementsRes.data;
        const achievementReviews = generateAchievementReviews(
            emailVerified,
            stripeConnected,
            publishedListings,
            displayName
        );

        const reviews = denormalisedResponseEntities(reviewsRes);
        const authorReviews = [...reviews];
        const rating = currentUser?.attributes?.profile?.publicData?.rating || null;

        dispatch(fetchOwnerInfoSuccess({
            totalListings: totalListings || 1,
            reviews: authorReviews,
            rating,
            verified: emailVerified,
            profile: currentUser,
        }));
    } catch (error) {
        dispatch(fetchOwnerInfoError(error));
    }
};


// Save Draft 
// Create listing in draft state
// NOTE: we want to keep it possible to include stock management field to the first wizard form.
// this means that there needs to be a sequence of calls:
// create, set stock, show listing (to get updated currentStock entity)
export function requestSaveListingDraft(data, config) {
    return async (dispatch, getState, sdk) => {
        try {
            const { imagesToUpload, base64Images, availabilityExceptions = [],
                title,
                description,
                price,
                availabilityPlan,
                geolocation,
                publicData
            } = data;

            dispatch(saveListingDraftRequest());
            let listingImages = [];
            let sharetribeImageIds = [];

            // Determine which image source to process.
            // imagesToUpload may contain a mix of raw File objects (step-1 uploads) and
            // plain AI-generated objects ({ secure_url, public_id, … }).
            // base64Images contains only AI-generated objects (no File objects).
            const sourceImages = imagesToUpload && imagesToUpload.length
                ? imagesToUpload
                : base64Images && base64Images.length
                    ? base64Images
                    : [];

            if (sourceImages.length) {
                const uploadResults = await Promise.all(
                    sourceImages.map(async (img) => {
                        if (img instanceof File) {
                            // User-uploaded file: push to both Sharetribe and Cloudinary.
                            const imageRes = await sdk.images.upload({ image: img });
                            const id = imageRes.data.data.id.uuid;
                            const result = await UploadImage({ file: img, id });
                            if (!result?.secure_url) return null;
                            return {
                                public_id: id,
                                secure_url: result.secure_url,
                                height: result.height,
                                width: result.width,
                                sharetribeImgId: id,
                            };
                        } else if (img.secure_url && !img.sharetribeImgId) {
                            // AI-generated image: already in Cloudinary from the backend.
                            // Upload only to Sharetribe and preserve the original public_id/URL.
                            const file = await urlToFile(img.secure_url, `listing-img-${uuid4()}`, 'image/jpeg');
                            const uploadRes = await sdk.images.upload({ image: file });
                            const id = uploadRes.data.data.id.uuid;
                            return {
                                public_id: img.public_id,
                                secure_url: img.secure_url,
                                height: img.height,
                                width: img.width,
                                sharetribeImgId: id,
                                ...(img.seoTags && { seoTags: img.seoTags }),
                            };
                        } else if (img.secure_url && img.sharetribeImgId) {
                            // Already fully uploaded — pass through as-is.
                            return img;
                        }
                        return null;
                    })
                );
                const validResults = uploadResults.filter(Boolean);
                sharetribeImageIds = validResults.map(r => r.sharetribeImgId);
                listingImages = validResults.map(({ public_id, secure_url, height, width, sharetribeImgId, seoTags }) => ({
                    public_id, secure_url, height, width, sharetribeImgId,
                    ...(seoTags && { seoTags }),
                }));
            }

            if (base64Images && base64Images.length) {
                publicData.generatedImages = base64Images.map(img => ({
                    secure_url: img.secure_url,
                    public_id: img.public_id,
                    height: img.height,
                    width: img.width,
                }));
            }

            const draftData = {
                title,
                description,
                price,
                availabilityPlan,
                geolocation,
                images: sharetribeImageIds,
                publicData: {
                    ...(publicData || {}),
                    listingImages
                }
            };

            const draftResponse = await sdk.ownListings.createDraft(draftData);

            // Add availability exceptions
            // Add availability exceptions
            // filter the exceptions to remove the empty ones
            const filteredAvailabilityExceptions = availabilityExceptions?.filter(exception => exception.start && exception.end);
            if (filteredAvailabilityExceptions.length) {
                await Promise.all(
                    filteredAvailabilityExceptions.map(exception =>
                        sdk.availabilityExceptions.create({
                            listingId: draftResponse.data.data.id.uuid,
                            start: new Date(exception.start),
                            end: new Date(exception.end),
                            seats: exception.seats,
                        })
                    )
                );
            };

            if (!draftResponse?.data?.data?.id) {
                throw new Error('Failed to create listing draft: No ID returned');
            }

            dispatch(saveListingDraftSuccess(draftResponse));
            dispatch(resetState())
            return draftResponse;
        } catch (error) {
            console.error('Failed to create listing draft:', error, {
                listingData: {
                    ...data,
                    publicData: data.publicData ? '[...large data omitted...]' : null
                }
            });
            dispatch(saveListingDraftError(storableError(error)));
            // Auto-clear error after 5 seconds (increased from 1s for better UX)
            setTimeout(() => dispatch(saveListingDraftError(storableError(null))), 5000)
            throw error;
        }
    };
};

export function requestCreateListing(data, config) {
    return async (dispatch, getState, sdk) => {
        dispatch(createListingRequest());
        try {
            const {
                imagesToUpload = [],
                base64Images = [],
                availabilityExceptions = [],
                images = [],
                ...rest
            } = data;

            let imageIds = [];
            let listingImagesMeta = [];

            // Upload images, can be file or base64
            // Priority: images array > imagesToUpload > base64Images
            const imagesToProcess = images.length
                ? images
                : imagesToUpload && imagesToUpload.length
                    ? imagesToUpload
                    : base64Images && base64Images.length
                        ? base64Images
                        : [];

            if (imagesToProcess.length) {
                const uploadResults = await Promise.all(
                    imagesToProcess.map(async (img, idx) => {
                        if (img instanceof File) {
                            const imageRes = await sdk.images.upload({ image: img });
                            const id = imageRes.data.data.id.uuid;
                            // Then upload to cloudinary
                            const result = await UploadImage({ file: img, id });
                            if (!result || !result.secure_url) {
                                return null;
                            }

                            const state = getState();
                            const storeName = state.user.currentUser?.attributes?.profile?.publicData?.storeName || '';
                            const listingContext = {
                                attributes: {
                                    title: rest.title || '',
                                    publicData: {
                                        city:      rest.publicData?.city || '',
                                        condition: rest.publicData?.condition || '',
                                        category:  rest.publicData?.category || '',
                                    },
                                },
                            };
                            const seoTags = buildListingImageSeoTags(listingContext, idx, storeName);

                            updateCloudinaryContext({ publicId: id, context: seoTags })
                                .catch(err => console.error(`Cloudinary context update failed for ${id}:`, err));

                            return { public_id: id, secure_url: result.secure_url, height: result.height, width: result.width, sharetribeImgId: id, seoTags };
                        } else if (img.secure_url && !img.sharetribeImgId) {
                            const file = await urlToFile(img.secure_url, `listing-img-${uuid4()}`, 'image/jpeg');
                            const res = await sdk.images.upload({ image: file });
                            if (!res || !res.data || !res.data.data || !res.data.data.id) {
                                return null;
                            }
                            return {
                                sharetribeImgId: res.data.data.id.uuid,
                                secure_url: img.secure_url,
                                public_id: img.public_id,
                                height: img.height,
                                width: img.width,
                                ...(img.seoTags && { seoTags: img.seoTags }),
                            };
                        }
                        return null;
                    })
                );

                // Filter out nulls AFTER Promise.all completes
                const newListingImages = uploadResults.filter(Boolean);

                // Ensure at least some images uploaded successfully
                if (newListingImages.length === 0 && imagesToProcess.length > 0) {
                    throw new Error('All image uploads failed. Please try again.');
                }

                imageIds = newListingImages.map(img => img.sharetribeImgId);
                listingImagesMeta = newListingImages;
            }

            // Upload instruction video if it's a file
            const publicData = { ...(rest.publicData || {}) };

            if (publicData?.instructions?.video instanceof File) {
                try {
                    const videoFile = publicData.instructions.video;
                    const uploadRes = await UploadVideo({
                        file: videoFile,
                        id: uuid4(),
                    });

                    const state = getState();
                    const storeName = state.user.currentUser?.attributes?.profile?.publicData?.storeName || '';
                    const listingContext = {
                        attributes: {
                            title: rest.title || '',
                            publicData: { city: publicData.city || '' },
                        },
                    };
                    const videoSeoTags = buildListingVideoSeoTags(listingContext, storeName);

                    publicData.instructions.video = {
                        public_id:    uploadRes.public_id,
                        asset_id:     uploadRes.asset_id,
                        width:        uploadRes.width,
                        height:       uploadRes.height,
                        playback_url: uploadRes.playback_url,
                        seoTags:      videoSeoTags,
                    };

                    updateCloudinaryContext({
                        publicId:     uploadRes.public_id,
                        context:      videoSeoTags,
                        resourceType: 'video',
                    }).catch(err => console.error('Cloudinary video context update failed:', err));

                    // Clear videoUrl when video file is uploaded (component already handles this, but ensure it's cleared here too)
                    if (publicData.instructions.videoUrl) {
                        publicData.instructions.videoUrl = null;
                    }
                } catch (videoErr) {
                    console.error('Video upload failed:', videoErr);
                }
            }
            // videoUrl is preserved automatically in publicData.instructions if it exists

            // Whatever happened above, only a real Cloudinary upload may be persisted.
            // A File whose upload failed serialises to `{}` on the wire, and an
            // empty-but-truthy `video` used to 500 the listing page during SSR.
            if (
                publicData.instructions &&
                typeof publicData.instructions === 'object' &&
                publicData.instructions.video != null &&
                !isUploadedInstructionVideo(publicData.instructions.video)
            ) {
                publicData.instructions = { ...publicData.instructions, video: null };
            }

            // Attach image metadata
            publicData.listingImages = listingImagesMeta;
            if (base64Images.length) {
                publicData.generatedImages = base64Images.map(img => ({
                    secure_url: img.secure_url,
                    public_id: img.public_id,
                    height: img.height,
                    width: img.width,
                }));
            }

            publicData.publishedAt = new Date().toISOString();

            // Prepare final listing payload
            const listingPayload = {
                ...rest,
                images: imageIds,
                publicData,
            };

            // Create listing
            const createRes = await sdk.ownListings.create(listingPayload, {
                expand: true,
            });

            const newListingId = createRes?.data?.data?.id;
            if (!newListingId) {
                throw new Error('Failed to create listing draft: No ID returned');
            }

            // Add availability exceptions
            // filter the exceptions to remove the empty ones
            const filteredAvailabilityExceptions = availabilityExceptions?.filter(exception => exception.start && exception.end);
            if (filteredAvailabilityExceptions.length) {
                await Promise.all(
                    filteredAvailabilityExceptions.map(exception =>
                        sdk.availabilityExceptions.create({
                            listingId: newListingId,
                            start: new Date(exception.start),
                            end: new Date(exception.end),
                            seats: exception.seats,
                        })
                    )
                );
            };
            dispatch(createListingSuccess(createRes));
            dispatch(resetState());

            // Save to Algolia and update current user
            const { currentUser } = getState().user;
            const currentUserCreatedAt = currentUser.attributes.createdAt;
            const {
                firstName,
                lastName,
                displayName,
                publicData: userPublicData = {},
                email,
            } = currentUser.attributes.profile;

            const listing = createRes.data.data || {};
            const attr = listing.attributes || {};
            const listingId = listing.id;

            if (attr.state == 'published') {
                // We have to know the position of listing
                // to define its default initial order.
                // It will be "total listings"

                // wait for the listing count to be available (2 sec delay to ensure listing is updated)
                await new Promise(resolve => setTimeout(resolve, 2000));
                sdk.ownListings.query({ states: LISTING_STATE_PUBLISHED })
                    .then(({ data }) => {
                        const { meta } = data || {};
                        const totalItems = meta?.totalItems;
                        const defaultOrder = totalItems ? totalItems : 0;

                        // Update in algolia
                        createAlgoliaData({
                            indexName: getAlgoliaIndexNameForLang((typeof window !== 'undefined' && localStorage.getItem('appLanguage')) || 'en'),
                            objectID: listingId.uuid,
                            state: attr.state,
                            _geoloc:
                                attr.geolocation && attr.geolocation.lat
                                    ? { lat: attr.geolocation.lat, lng: attr.geolocation.lng }
                                    : {},
                            title: attr.title,
                            description: attr.description.slice(0, 999),
                            price: attr.price,
                            publicData: {
                                ...filterPublicDataToAlgolia(attr.publicData, LISTING_ALOGLIA_REMOVABLE_FIELDS),
                            },
                            metadata: attr.metadata,
                            user: {
                                id: currentUser.id.uuid,
                                firstName,
                                lastName,
                                email,
                                displayName,
                                publicData: filterListingUserPublicDataForAlgolia(userPublicData),
                            },
                            createdAt: attr.createdAt,
                            timestamp: moment(attr.createdAt).unix(),
                            order: defaultOrder,
                        });

                        // Sync all 4 locale Algolia indices with locale-correct store category hierarchy.
                        // createAlgoliaData only targets the current-UI-language index; this call
                        // ensures FR/ES/ZH visitors immediately see correct breadcrumbs.
                        if (attr.publicData?.storeCategoryId) {
                            syncListingAlgolia(listingId.uuid).catch(err =>
                                console.error('syncListingAlgolia after wizard publish failed:', err)
                            );
                        }

                        // Update in sharetribe
                        sdk.ownListings.update({
                            id: listingId.uuid,
                            publicData: {
                                order: defaultOrder,
                            },
                        });

                        // update mixpanel for total published listings count
                        setPublishedListingsCount(totalItems, true);
                    });

                // Create user record in Algolia when he first time
                // publish his listing. Need to know the total active
                // listings so his store can be ranked.
                userActiveListings(currentUser.id.uuid)
                    .then(res => {
                        const { totalListings } = res.data || {};

                        // If this is the first listing and user doesn't have location, copy from listing
                        if (totalListings === 1) {
                            const hasUserLocation = userPublicData.address &&
                                userPublicData.origin?.lat &&
                                userPublicData.origin?.lng;

                            if (!hasUserLocation) {
                                const listingLocation = attr.publicData?.location?.address;
                                const listingCity = attr.publicData?.city;
                                const listingCountry = attr.publicData?.country;
                                const listingPostcode = attr.publicData?.postcode;
                                const listingState = attr.publicData?.state || attr.publicData?.region;
                                const listingOrigin = attr.geolocation;

                                if (listingLocation && listingOrigin) {
                                    // Update user profile with listing location
                                    sdk.currentUser.updateProfile({
                                        publicData: {
                                            address: listingLocation,
                                            ...(listingCity && { city: listingCity }),
                                            ...(listingCountry && { country: listingCountry }),
                                            ...(listingPostcode && { postcode: listingPostcode }),
                                            ...(listingState && { state: listingState }),
                                            origin: {
                                                lat: listingOrigin.lat,
                                                lng: listingOrigin.lng
                                            }
                                        }
                                    })
                                        .then(() => {
                                            console.log('User profile updated with first listing location');
                                        })
                                        .catch(err => {
                                            console.error('Failed to update user profile with location:', err);
                                        });
                                }
                            }
                        }

                        // User
                        createAlgoliaData({
                            indexName: process.env.REACT_APP_ALGOLIA_USERS_INDEX,
                            objectID: currentUser.id.uuid,
                            _geoloc:
                                userPublicData.origin?.lng && userPublicData.origin?.lat
                                    ? {
                                        lat: userPublicData.origin.lat,
                                        lng: userPublicData.origin.lng,
                                    }
                                    : {},
                            firstName,
                            lastName,
                            displayName,
                            email,
                            publicData: filterUserPublicDataForAlgolia(userPublicData, { totalListings }),
                            ...(userPublicData.profileImage
                                ? { profileImage: userPublicData.profileImage }
                                : {}),
                            createdAt: currentUserCreatedAt,
                            timestamp: moment(currentUserCreatedAt).unix(),
                        });
                    });
            };
            return createRes.data.data;
        } catch (error) {
            console.error('Failed to create listing:', error, {
                listingDataSummary: {
                    keys: Object.keys(data),
                    publicDataKeys: data.publicData ? Object.keys(data.publicData) : null,
                },
            });
            dispatch(createListingError(error));
            // Auto-clear error after 5 seconds (increased from 1s for better UX)
            setTimeout(() => dispatch(createListingError(null)), 5000)
            throw error;
        }
    };
}


export const requestGetCredits = (listingToken) => async (dispatch, getState, sdk) => {
    try {
        dispatch(getCreditsRequest());
        const credits = await getCredits(listingToken);
        dispatch(getCreditsSuccess(credits.remainingCredits));
    } catch (error) {
        dispatch(getCreditsError(error));
    }
};

export const requestEnhanceField = (enhanceFieldParams) => async (dispatch, getState, sdk) => {
    try {
        dispatch(enhanceFieldRequest(enhanceFieldParams.fieldName));
        const { data } = await enhanceField(enhanceFieldParams);
        const remainingCredits = data.remainingCredits;

        dispatch(enhanceFieldSuccess(data.fieldName, data.enhancedContent));
        dispatch(getCreditsSuccess(remainingCredits));

        return {
            fieldName: data.fieldName,
            enhancedContent: data.enhancedContent
        };
    } catch (error) {
        dispatch(enhanceFieldError(enhanceFieldParams.fieldName, error));
        throw error;
    }
};

export const requestGenerateAIImages = ({ listingId, ...params }) => async (dispatch, getState, sdk) => {
    try {
        dispatch(generateAIImagesRequest());
        const { images, remainingCredits } = await generateAIImages(params);
        const generatedImages = images.map(img => ({
            secure_url: img.imageUrl,
            public_id: img.publicId,
            height: img.height,
            width: img.width,
            ...(img.seoTags && { seoTags: img.seoTags }),
        }));
        dispatch(getCreditsSuccess(remainingCredits));
        dispatch(generateAIImagesSuccess(generatedImages));
        return generatedImages;
    } catch (error) {
        dispatch(generateAIImagesError(error));
    }
};


// Load data
export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
    try {
        const promises = [];
        promises.push(dispatch(fetchOwnerInfo()));

        if (params.listingToken) {
            promises.push(dispatch(requestGetCredits(params.listingToken)));
        }

        // Fetch exchange rates, do not fetch if already exists.
        const { exchangeRates } = getState().exchangeRate;

        if (typeof window != 'undefined' && exchangeRates && exchangeRates.length == 0) {
            promises.push(dispatch(fetchExchangeRates()));
        };

        await Promise.all(promises);
    } catch (error) {
        console.error('Error loading owner info:', error);
    }
};
